/* eslint-disable max-lines-per-function, max-lines */
import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, Observable, of, race, switchMap } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import {
  disqualifyReasonIdScope,
  DisqualifyReasonsAutomationsActions,
} from '@core/disqualify-reasons-data';
import { I18nService } from '@core/i18n-legacy';
import { jobIdScope, JobWorkflowAutomationsActions } from '@core/offers-data';
import { DialogService } from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';
import {
  AutomationTriggerType,
  WorkflowAutomationAdditionalData,
  WorkflowAutomationCreatePayload,
  WorkflowAutomationUpdatePayload,
} from '@core/workflow-automations-types';
import {
  jobTemplateIdScope,
  JobTemplateWorkflowAutomationsActions,
} from '@recruitee/job-templates-data';
import {
  PipelineTemplateStageAutomationsActions,
  stageIdScope,
} from '@recruitee/workflow-templates-data';

@Injectable({ providedIn: 'root' })
export class AutomationActionsDispatcher {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly dialogService = inject(DialogService);
  private readonly i18n = inject(I18nService);

  public createAutomation(
    automation: WorkflowAutomationCreatePayload,
    additionalData: WorkflowAutomationAdditionalData,
  ): Observable<boolean> {
    const correlationId = generateUuid();

    if (automation.trigger.type === AutomationTriggerType.CandidateMoved) {
      this.store.dispatch(
        PipelineTemplateStageAutomationsActions.createOnePessimistic({
          payload: {
            data: automation,
            additionalData,
            correlationId,
            scope: stageIdScope(<number>automation.trigger.data['stage_id']),
          },
        }),
      );
      const success$ = this.actions$.pipe(
        ofType(PipelineTemplateStageAutomationsActions.createOnePessimisticSuccess),
      );
      const failed$ = this.actions$.pipe(
        ofType(PipelineTemplateStageAutomationsActions.createOnePessimisticFailed),
      );
      return race(success$.pipe(map(() => true)), failed$.pipe(map(() => false)));
    }

    if (automation.trigger.type === AutomationTriggerType.CandidateDisqualified) {
      this.store.dispatch(
        DisqualifyReasonsAutomationsActions.createOnePessimistic({
          payload: {
            data: automation,
            additionalData,
            correlationId,
            scope: disqualifyReasonIdScope(<number>automation.trigger.data['disqualify_reason_id']),
          },
        }),
      );
      const success$ = this.actions$.pipe(
        ofType(DisqualifyReasonsAutomationsActions.createOnePessimisticSuccess),
      );
      const failed$ = this.actions$.pipe(
        ofType(DisqualifyReasonsAutomationsActions.createOnePessimisticFailed),
      );
      return race(success$.pipe(map(() => true)), failed$.pipe(map(() => false)));
    }

    const { jobId, jobTemplateId } = additionalData;

    if (jobId) {
      this.store.dispatch(
        JobWorkflowAutomationsActions.createOnePessimistic({
          payload: {
            data: automation,
            jobId,
            correlationId,
            scope: jobIdScope(jobId),
          },
        }),
      );

      const success$ = this.actions$.pipe(
        ofType(JobWorkflowAutomationsActions.createOnePessimisticSuccess),
      );
      const failed$ = this.actions$.pipe(
        ofType(JobWorkflowAutomationsActions.createOnePessimisticFailed),
      );
      return race(success$.pipe(map(() => true)), failed$.pipe(map(() => false)));
    }

    if (jobTemplateId) {
      this.store.dispatch(
        JobTemplateWorkflowAutomationsActions.createOnePessimistic({
          payload: {
            data: automation,
            jobTemplateId,
            correlationId,
            scope: jobTemplateIdScope(jobTemplateId),
          },
        }),
      );

      const success$ = this.actions$.pipe(
        ofType(JobTemplateWorkflowAutomationsActions.createOnePessimisticSuccess),
      );
      const failed$ = this.actions$.pipe(
        ofType(JobTemplateWorkflowAutomationsActions.createOnePessimisticFailed),
      );
      return race(success$.pipe(map(() => true)), failed$.pipe(map(() => false)));
    }

    return of(false);
  }

  public updateAutomation(
    automation: WorkflowAutomationUpdatePayload,
    additionalData: WorkflowAutomationAdditionalData,
  ): Observable<boolean> {
    const correlationId = generateUuid();

    if (automation.trigger.type === AutomationTriggerType.CandidateMoved) {
      this.store.dispatch(
        PipelineTemplateStageAutomationsActions.updateOnePessimistic({
          payload: {
            data: automation,
            additionalData,
            correlationId,
            scope: stageIdScope(<number>automation.trigger.data['stage_id']!),
          },
        }),
      );
      const success$ = this.actions$.pipe(
        ofType(PipelineTemplateStageAutomationsActions.updateOnePessimisticSuccess),
      );
      const failed$ = this.actions$.pipe(
        ofType(PipelineTemplateStageAutomationsActions.updateOnePessimisticFailed),
      );
      return race(success$.pipe(map(() => true)), failed$.pipe(map(() => false)));
    }

    if (automation.trigger.type === AutomationTriggerType.CandidateDisqualified) {
      this.store.dispatch(
        DisqualifyReasonsAutomationsActions.updateOnePessimistic({
          payload: {
            data: automation,
            additionalData,
            correlationId,
            scope: disqualifyReasonIdScope(<number>automation.trigger.data['disqualify_reason_id']),
          },
        }),
      );
      const success$ = this.actions$.pipe(
        ofType(DisqualifyReasonsAutomationsActions.updateOnePessimisticSuccess),
      );
      const failed$ = this.actions$.pipe(
        ofType(DisqualifyReasonsAutomationsActions.updateOnePessimisticFailed),
      );
      return race(success$.pipe(map(() => true)), failed$.pipe(map(() => false)));
    }

    const { jobId, jobTemplateId } = additionalData;
    if (jobId) {
      this.store.dispatch(
        JobWorkflowAutomationsActions.updateOnePessimistic({
          payload: {
            data: automation,
            jobId,
            correlationId,
            scope: jobIdScope(jobId),
          },
        }),
      );
      const success$ = this.actions$.pipe(
        ofType(JobWorkflowAutomationsActions.updateOnePessimisticSuccess),
      );
      const failed$ = this.actions$.pipe(
        ofType(JobWorkflowAutomationsActions.updateOnePessimisticFailed),
      );
      return race(success$.pipe(map(() => true)), failed$.pipe(map(() => false)));
    }
    if (jobTemplateId) {
      this.store.dispatch(
        JobTemplateWorkflowAutomationsActions.updateOnePessimistic({
          payload: {
            data: automation,
            jobTemplateId,
            correlationId,
            scope: jobTemplateIdScope(jobTemplateId),
          },
        }),
      );
      const success$ = this.actions$.pipe(
        ofType(JobTemplateWorkflowAutomationsActions.updateOnePessimisticSuccess),
      );
      const failed$ = this.actions$.pipe(
        ofType(JobTemplateWorkflowAutomationsActions.updateOnePessimisticFailed),
      );
      return race(success$.pipe(map(() => true)), failed$.pipe(map(() => false)));
    }

    return of(false);
  }

  public deleteAutomation(
    automationId: number,
    triggerType: AutomationTriggerType,
    additionalData: WorkflowAutomationAdditionalData,
  ): Observable<boolean> {
    return this.dialogService
      .error({
        title: this.i18n.translate('app.workflow_automations.confirm_delete_dialog.title'),
        agreeLabel: this.i18n.translate(
          'app.workflow_automations.confirm_delete_dialog.agree_label',
        ),
        message: this.i18n.translate('app.workflow_automations.confirm_delete_dialog.message'),
        safeFooter: false,
      })
      .pipe(
        filterBoolean,
        switchMap(() => {
          if (triggerType === AutomationTriggerType.CandidateMoved) {
            this.store.dispatch(
              PipelineTemplateStageAutomationsActions.removeOnePessimistic({
                payload: {
                  data: automationId,
                  additionalData,
                },
              }),
            );
            const success$ = this.actions$.pipe(
              ofType(PipelineTemplateStageAutomationsActions.removeOnePessimisticSuccess),
            );
            const failed$ = this.actions$.pipe(
              ofType(PipelineTemplateStageAutomationsActions.removeOnePessimisticFailed),
            );
            return race(success$.pipe(map(() => true)), failed$.pipe(map(() => false)));
          }

          if (triggerType === AutomationTriggerType.CandidateDisqualified) {
            this.store.dispatch(
              DisqualifyReasonsAutomationsActions.removeOnePessimistic({
                payload: {
                  data: automationId,
                  additionalData,
                },
              }),
            );
            const success$ = this.actions$.pipe(
              ofType(DisqualifyReasonsAutomationsActions.removeOnePessimisticSuccess),
            );
            const failed$ = this.actions$.pipe(
              ofType(DisqualifyReasonsAutomationsActions.removeOnePessimisticFailed),
            );
            return race(success$.pipe(map(() => true)), failed$.pipe(map(() => false)));
          }

          const { jobId, jobTemplateId } = additionalData;
          if (jobId) {
            this.store.dispatch(
              JobWorkflowAutomationsActions.removeOnePessimistic({
                payload: {
                  data: automationId,
                  jobId,
                },
              }),
            );
            const success$ = this.actions$.pipe(
              ofType(JobWorkflowAutomationsActions.removeOnePessimisticSuccess),
            );
            const failed$ = this.actions$.pipe(
              ofType(JobWorkflowAutomationsActions.removeOnePessimisticFailed),
            );
            return race(success$.pipe(map(() => true)), failed$.pipe(map(() => false)));
          }

          if (jobTemplateId) {
            this.store.dispatch(
              JobTemplateWorkflowAutomationsActions.removeOnePessimistic({
                payload: {
                  data: automationId,
                  jobTemplateId,
                },
              }),
            );
            const success$ = this.actions$.pipe(
              ofType(JobTemplateWorkflowAutomationsActions.removeOnePessimisticSuccess),
            );
            const failed$ = this.actions$.pipe(
              ofType(JobTemplateWorkflowAutomationsActions.removeOnePessimisticFailed),
            );

            return race(success$.pipe(map(() => true)), failed$.pipe(map(() => false)));
          }

          return of(false);
        }),
      );
  }

  public duplicateAutomation(
    automationId: number,
    triggerType: AutomationTriggerType,
    additionalData: WorkflowAutomationAdditionalData,
  ): Observable<boolean | number> {
    if (triggerType === AutomationTriggerType.CandidateMoved) {
      this.store.dispatch(
        PipelineTemplateStageAutomationsActions.duplicate({
          payload: {
            data: automationId,
            additionalData,
            scope: stageIdScope(additionalData.stageId!),
          },
        }),
      );
      const success$ = this.actions$.pipe(
        ofType(PipelineTemplateStageAutomationsActions.duplicateSuccess),
      );
      const failed$ = this.actions$.pipe(
        ofType(PipelineTemplateStageAutomationsActions.duplicateFailed),
      );
      return race(success$.pipe(map(data => data.payload.data.id)), failed$.pipe(map(() => false)));
    }

    if (triggerType === AutomationTriggerType.CandidateDisqualified) {
      this.store.dispatch(
        DisqualifyReasonsAutomationsActions.duplicate({
          payload: {
            data: automationId,
            additionalData,
            scope: disqualifyReasonIdScope(additionalData.disqualifyReasonId!),
          },
        }),
      );
      const success$ = this.actions$.pipe(
        ofType(DisqualifyReasonsAutomationsActions.duplicateSuccess),
      );
      const failed$ = this.actions$.pipe(
        ofType(DisqualifyReasonsAutomationsActions.duplicateFailed),
      );
      return race(success$.pipe(map(data => data.payload.data.id)), failed$.pipe(map(() => false)));
    }

    const { jobId, jobTemplateId } = additionalData;

    if (jobId) {
      this.store.dispatch(
        JobWorkflowAutomationsActions.duplicate({
          payload: {
            data: automationId,
            additionalData,
            scope: jobIdScope(jobId),
          },
        }),
      );
      const success$ = this.actions$.pipe(ofType(JobWorkflowAutomationsActions.duplicateSuccess));
      const failed$ = this.actions$.pipe(ofType(JobWorkflowAutomationsActions.duplicateFailed));
      return race(success$.pipe(map(data => data.payload.data.id)), failed$.pipe(map(() => false)));
    }

    if (jobTemplateId) {
      this.store.dispatch(
        JobTemplateWorkflowAutomationsActions.duplicate({
          payload: {
            data: automationId,
            additionalData,
            scope: jobTemplateIdScope(jobTemplateId),
          },
        }),
      );
      const success$ = this.actions$.pipe(
        ofType(JobTemplateWorkflowAutomationsActions.duplicateSuccess),
      );
      const failed$ = this.actions$.pipe(
        ofType(JobTemplateWorkflowAutomationsActions.duplicateFailed),
      );

      return race(success$.pipe(map(data => data.payload.data.id)), failed$.pipe(map(() => false)));
    }

    return of(false);
  }
}
